import { useMemo } from "react";

import { AppButton, useBalanceChecker, useLoopWindAlerts } from "app/components/common";
import { COPY, DOCS } from "app/constants";
import { Column, TagTextAlert, TextExternalLink } from "@bridgesplit/ui";
import {
    getLoopBorrowCapDetails,
    isWhirlpoolMetadata,
    useTokenListContext,
    useLoopscaleWalletContext,
    TokenRestrictedActions,
    getDerivedLoopAssetMints,
    isLeverageLoop
} from "@bridgesplit/abf-react";
import { bsMath, Result, tokenSymbolToUrlSlug } from "@bridgesplit/utils";
import { AppDialog, useAppDialog } from "app/utils";
import { TransactionTypes } from "app/types";
import { usePermissionedLoanCheck, useTokenActionRestrictionsConfig } from "app/hooks";
import { TokenRestrictionAlerts } from "app/components/tokens/TokenRestrictionAlerts";

import {
    useCalculateFairAndMarketValues,
    useLoopWind,
    useManualWindBorrow,
    useWindSimulationBalanceChanges
} from "./util";
import { useWindContext } from "./WindContext";
import { useLoopContext } from "../LoopContext";
import WindRiskMessage from "./WindRiskMessage";
import { LoopMode } from "./types";
import { getCtaError, getAlertError } from "./WindCtaErrors";
import { useWindMinDepositView } from "./useWindMinDepositView";

const PNL_IMPACT_THRESHOLD = 0.05;

export default function WindCta() {
    const wind = useLoopWind();
    const manualBorrow = useManualWindBorrow();
    const { open: openDialog } = useAppDialog();
    const {
        loopMode,
        form,
        isQuoteFetching,
        quote,
        externalQuote,
        collateralAmount,
        externalQuoteLoading,
        externalQuoteError,
        oraclePrices,
        leveragedCollateralAmountToDeposit,
        LoopWinder,
        bestBorrowQuote,
        borrowQuoteFetching
    } = useWindContext();

    const { loopExpanded } = useLoopContext();
    const { getMetadata } = useTokenListContext();
    const {
        data: { userWalletAddress: user }
    } = useLoopscaleWalletContext();
    const simulationError = useWindSimulationBalanceChanges({
        loopExpanded,
        form,
        externalQuote,
        quote,
        leveragedCollateralAmountToDeposit,
        loopWinder: LoopWinder,
        activeWallet: user,
        getMetadata,
        skip: loopMode === LoopMode.Manual
    });
    const metadata = getMetadata(form.inputToken?.mint);
    const isOrcaPosition = isWhirlpoolMetadata(metadata);

    // For Manual mode, check collateral balance; for auto mode, check input balance
    const isManualMode = loopMode === LoopMode.Manual;
    const balanceCheckMint = isManualMode ? loopExpanded?.collateralToken.mint : form.inputToken?.mint;
    const balanceCheckAmount = isManualMode ? form.collateralAmount : form.inputAmount;

    const { insufficientBalance } = useBalanceChecker({
        mint: balanceCheckMint,
        amount: balanceCheckAmount,
        skip: isOrcaPosition
    });

    const { windDisabledWithMessage } = useLoopWindAlerts(loopExpanded);

    const tokenRestrictionsConfig = useTokenActionRestrictionsConfig({
        mints: getDerivedLoopAssetMints(loopExpanded),
        primaryRelevantAction: TokenRestrictedActions.LoopStart,
        secondaryRelevantAction: TokenRestrictedActions.LoopClose
    });

    const { totalFairMarketValueUsd, totalReceivedMarketValueUsd } = useCalculateFairAndMarketValues();

    const collateralInputAmount = LoopWinder.getCollateralInputAmount({
        loopExpanded,
        inputAmount: form.inputAmount,
        inputToken: form.inputToken,
        oraclePrices
    });

    const formInputMint = LoopWinder.getFormInputMint({
        loopExpanded,
        form
    });

    const collateralOracle = oraclePrices?.get(loopExpanded?.collateralToken.mint || "");
    // Data-driven per-deposit minimum from configured swap routes into the collateral (e.g. Glow vault).
    const routeMinDeposit = useWindMinDepositView(loopExpanded?.collateralToken.mint);
    const inputTokenOracle = oraclePrices?.get(form.inputToken?.mint || "");
    const materialNegativeMarketDifference = materialFairMarketValueDifference({
        totalFairMarketValueUsd,
        totalReceivedMarketValueUsd,
        unleveredCollateralAmount: collateralInputAmount,
        inputPrice: collateralOracle?.usdPrice
    });

    const showMaterialNegativeMarketDifference = isManualMode
        ? false
        : !externalQuoteLoading && !externalQuoteError && externalQuote && quote && materialNegativeMarketDifference;

    const borrowCapDetails = useMemo(() => {
        if (!loopExpanded) return undefined;
        return getLoopBorrowCapDetails(loopExpanded);
    }, [loopExpanded]);

    // Check if user has a permissioned RWA token
    const { isPermissionedToken } = usePermissionedLoanCheck(
        loopExpanded?.principalToken,
        loopExpanded?.collateralToken
    );
    const ctaError = useMemo(() => {
        return getCtaError({
            loopMode,
            form,
            borrowQuoteFetching,
            isQuoteFetching,
            loopExpanded,
            collateralAmount,
            bestBorrowQuote,
            quote,
            externalQuote
        });
    }, [
        loopMode,
        form,
        borrowQuoteFetching,
        isQuoteFetching,
        loopExpanded,
        collateralAmount,
        bestBorrowQuote,
        quote,
        externalQuote
    ]);

    const alertInfo = useMemo(() => {
        if (showMaterialNegativeMarketDifference)
            return (
                <>
                    Current conditions may result in unfavorable execution. Check <i>Execution impact</i> for details,
                    as this could reduce your PnL.
                </>
            );
    }, [showMaterialNegativeMarketDifference]);

    const alertError = useMemo(() => {
        return getAlertError({
            isPermissionedToken,
            loopMode,
            form,
            isQuoteFetching,
            user,
            borrowCapDetails,
            loopExpanded,
            windDisabledWithMessage,
            collateralAmount,
            bestBorrowQuote,
            quote,
            externalQuote,
            externalQuoteError,
            collateralOracle,
            collateralInputAmount,
            leveragedCollateralAmountToDeposit,
            routeMinDeposit,
            inputTokenSymbol: form.inputToken?.symbol,
            inputTokenUsdPrice: inputTokenOracle?.usdPrice,
            insufficientBalance,
            borrowQuoteFetching
        });
    }, [
        isPermissionedToken,
        loopMode,
        form,
        isQuoteFetching,
        user,
        borrowCapDetails,
        loopExpanded,
        windDisabledWithMessage,
        collateralAmount,
        bestBorrowQuote,
        quote,
        externalQuote,
        externalQuoteError,
        collateralOracle,
        collateralInputAmount,
        leveragedCollateralAmountToDeposit,
        routeMinDeposit,
        inputTokenOracle,
        insufficientBalance,
        borrowQuoteFetching
    ]);
    const showSimulationFailedWarning = !!simulationError && !alertError;
    const simulationWarningMessage =
        simulationError?.message ?? "Transaction may fail. Please try increasing slippage.";
    const manualLoopMessage = "Transaction may fail. Please try manual looping";
    const manualLoopUrl = loopExpanded
        ? `https://app.loopscale.com/market/${tokenSymbolToUrlSlug(
              loopExpanded.principalToken.symbol
          )}?role=borrow&collateral=${loopExpanded.collateralToken.mint}`
        : undefined;

    const ctaText = isManualMode ? "Borrow" : COPY.LOOP_TERM_ACTION;

    const submitWind = isManualMode
        ? manualBorrow
        : () =>
              wind({
                  collateralMint: externalQuote?.quote.outputMint,
                  inputMint: formInputMint
              });

    const borrowApy = quote?.bestQuote?.apy;
    const netApyPct = quote?.netApyPct;
    const negativeNetApy = netApyPct !== undefined && netApyPct < 0;
    const requireRiskConfirm = negativeNetApy && !isLeverageLoop(loopExpanded);

    const onCtaClick = () => {
        if (requireRiskConfirm) {
            openDialog(AppDialog.BorrowRiskConfirm, {
                netApyPct,
                borrowApy,
                avgApy: undefined,
                negativeNetApy,
                borrowApyAboveAvg: false,
                onConfirm: submitWind
            });
            return Promise.resolve(Result.ok());
        }
        return submitWind();
    };

    return (
        <Column spacing={1.5}>
            <AppButton
                isTransaction={!requireRiskConfirm}
                transactionType={TransactionTypes.EarnBorrow}
                asyncCta={{
                    onClickWithResult: onCtaClick,
                    options: { alertOnError: true }
                }}
                color="secondary"
                disabled={!!alertError || !!ctaError}
            >
                {ctaError || ctaText}
            </AppButton>
            <WindRiskMessage />
            {alertError && (
                <TagTextAlert color="warning" textSx={{ display: "inline-block" }} icon="warning">
                    {alertError}
                </TagTextAlert>
            )}
            {showSimulationFailedWarning && (
                <TagTextAlert color="warning" textSx={{ display: "inline-block" }} icon="warning">
                    {simulationError?.showManualLoopLink && manualLoopUrl ? (
                        <>
                            {manualLoopMessage}{" "}
                            <TextExternalLink href={manualLoopUrl} isPrimary underlineLink>
                                here
                            </TextExternalLink>
                            . Learn about{" "}
                            <TextExternalLink href={DOCS.manualLoopTutorial} isPrimary underlineLink>
                                manual looping
                            </TextExternalLink>
                        </>
                    ) : (
                        simulationWarningMessage
                    )}
                </TagTextAlert>
            )}
            {alertInfo && (
                <TagTextAlert color="secondary" textSx={{ display: "inline-block" }} icon="tooltip">
                    {alertInfo}
                </TagTextAlert>
            )}
            {user && tokenRestrictionsConfig && (
                <TokenRestrictionAlerts tokenRestrictionConfig={tokenRestrictionsConfig} />
            )}
        </Column>
    );
}

function materialFairMarketValueDifference({
    totalFairMarketValueUsd,
    totalReceivedMarketValueUsd,
    unleveredCollateralAmount,
    inputPrice
}: {
    totalFairMarketValueUsd: number | undefined;
    totalReceivedMarketValueUsd: number | undefined;
    unleveredCollateralAmount: number | undefined;
    inputPrice: number | undefined;
}) {
    if (!totalFairMarketValueUsd || !totalReceivedMarketValueUsd || !unleveredCollateralAmount || !inputPrice)
        return false;
    const diff = bsMath.sub(totalFairMarketValueUsd, totalReceivedMarketValueUsd);
    if (!diff || diff <= 0) return false;
    const unleveredCollateralAmountUsd = unleveredCollateralAmount * inputPrice;
    if (unleveredCollateralAmountUsd === 0) return false;

    return diff / unleveredCollateralAmountUsd > PNL_IMPACT_THRESHOLD;
}
